<template>
  <div class="job-notes flex flex-col gap-3">
    <div class="flex items-center justify-between gap-2">
      <div class="font-medium">Job Notes</div>
      <sera-button
        data-cy="add-note"
        label="Add Note"
        icon="plus"
        iconRight
        @click="openAdd"
      />
    </div>

    <TextField
      v-model="search"
      class="mb-0"
      placeholder="Search notes"
      search
      no-label
    />
    <div class="flex items-center justify-between">
      <div class="sort-cont text-sm" data-cy="sort-notes" @click="toggleSort">
        Sort
        <i
          class="fad fa-sort-alt sorting-icon"
          :class="{ 'asc-icon': sort === 'asc' }"
        />
      </div>
      <ToggleSwitch v-model="filter" label-on="All" label-off="Standard" />
    </div>

    <div v-if="loading" class="py-6">
      <Spinner message="Loading notes..." />
    </div>

    <div
      v-else-if="notes.length"
      class="bg-white rounded-lg border divide-y"
      data-cy="job-notes-list"
    >
      <div v-for="note in notes" :key="note.id" class="p-3" data-cy="job-note">
        <div class="flex items-start justify-between gap-2">
          <div class="flex items-start gap-2 min-w-0">
            <i
              v-if="note.pinned"
              class="fa-solid fa-thumbtack text-primary text-xs mt-1 shrink-0"
            />
            <div class="min-w-0">
              <div class="flex items-center gap-1.5 mb-0.5 flex-wrap">
                <span class="text-xs text-gray-500">
                  {{ note.user ? note.user.full_name : "System Message" }}
                </span>
                <span class="text-xs text-gray-400">
                  {{ formatDate(note.created_at) }}
                </span>
                <i
                  v-if="!note.customer_visible"
                  class="fa-regular fa-eye-slash text-xs text-gray-400"
                  title="Internal only — not visible to customer"
                />
              </div>
              <!-- Notes are stored as HTML (Broccoli online booking and the
                   rich-text editor both post HTML); render it the same way
                   Comments/Comment.vue does rather than escaping the markup. -->
              <!-- eslint-disable vue/no-v-html -->
              <div
                class="text-sm text-gray-700 break-words whitespace-pre-line"
                v-html="note.comment"
              />
              <!-- eslint-enable vue/no-v-html -->
            </div>
          </div>
          <CommentActions
            v-if="allowEdit"
            :comment="note"
            @edit-comment="openEdit(note)"
            @delete-comment="confirmDelete(note.id)"
            @toggle-visibility="toggleVisibility(note)"
            @toggle-important="togglePinned(note)"
          />
        </div>
      </div>
    </div>

    <div
      v-else
      class="bg-white rounded-lg border p-4 text-sm text-gray-500 text-center"
    >
      No job notes yet.
    </div>

    <PaginationControls
      v-if="pager"
      :pager="pager"
      no-page-length
      @update:data="updateData"
    />

    <CommentFormModal
      v-if="addModal"
      :commentable-type="commentableType"
      :commentable-id="commentableId"
      @hide-modal="addModal = false"
      @created="onCreated"
    />
    <CommentFormModal
      v-if="editComment"
      :comment="editComment"
      :commentable-type="commentableType"
      :commentable-id="commentableId"
      @updated="onUpdated"
      @cancel="editComment = null"
      @hide-modal="editComment = null"
    />
    <Modal
      v-if="deleteId"
      title="Delete Note"
      no-submit
      @hide-modal="deleteId = null"
      @delete="doDelete"
    >
      <div>
        This note will be permanently deleted and you will not be able to undo
        this action. Are you sure?
      </div>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { debounce } from "lodash";
import moment from "moment";
import API from "serviceshift-ui/api-client";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { EventBus } from "@/lib/event_bus";
import Pagination from "@/lib/pagination";

import Modal from "@/components/Modal.vue";
import PaginationControls from "@/components/PaginationControls.vue";
import Spinner from "@/components/Spinner2.vue";
import TextField from "@/components/TextField.vue";
import ToggleSwitch from "@/components/ToggleSwitch.vue";
import CommentActions from "@/Comments/CommentActions.vue";
import CommentFormModal from "@/Comments/CommentFormModal.vue";
import { Comment } from "@/typings/comment";

const props = withDefaults(
  defineProps<{
    commentableId: number;
    commentableType?: string;
    allowEdit?: boolean;
  }>(),
  { commentableType: "Job", allowEdit: true }
);
const emit = defineEmits(["refresh"]);

const pager = ref<Pagination | null>(null);
const dateGroups = ref<Record<string, Comment[]>>({});
const pinned = ref<Comment[]>([]);
const search = ref("");
const filter = ref(false);
const sort = ref<"asc" | "desc">("desc");
const loading = ref(true);
const addModal = ref(false);
const editComment = ref<Comment | null>(null);
const deleteId = ref<number | null>(null);

const notes = computed<Comment[]>(() => {
  const dir = sort.value === "asc" ? 1 : -1;
  const byDate = (a: Comment, b: Comment) =>
    dir * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  const pinnedSorted = [...pinned.value].sort(byDate);
  const dated = Object.values(dateGroups.value).flat().sort(byDate);
  return [...pinnedSorted, ...dated];
});

// The by-date endpoint returns comments keyed by ISO date, plus a `pinned`
// bucket — all values are Comment[].
type CommentsByDate = Record<string, Comment[]>;

const updateData = (data?: CommentsByDate | null) => {
  if (!data) {
    dateGroups.value = {};
    pinned.value = [];
    return;
  }
  const { pinned: pinnedData, ...groups } = data;
  pinned.value = pinnedData || [];
  dateGroups.value = groups;
};

const fetchData = async () => {
  loading.value = true;
  try {
    const res = await pager.value?.getData();
    updateData(res?.data.data);
  } catch (e) {
    // Leave the existing list in place on a failed refresh.
  }
  loading.value = false;
};

onMounted(() => {
  pager.value = new Pagination({
    baseURL: "/admin/v4/comments/by_date",
    filteredBy: [
      { key: "commentable_id", value: props.commentableId },
      { key: "commentable_type", value: props.commentableType },
      { key: "all", value: filter.value }
    ],
    sortedBy: [{ key: "created_at", direction: "desc" }]
  });
  fetchData();
  EventBus.$on("refresh-comments", fetchData);
});

onBeforeUnmount(() => EventBus.$off("refresh-comments", fetchData));

const formatDate = (date: string) => moment(date).format("MMM D, YYYY");

const toggleSort = () => {
  sort.value = sort.value === "desc" ? "asc" : "desc";
};

const openAdd = () => {
  addModal.value = true;
};
const openEdit = (note: Comment) => {
  editComment.value = note;
};
const confirmDelete = (id: number) => {
  deleteId.value = id;
};

const refreshAfterMutation = () => {
  fetchData();
  emit("refresh");
};

const onCreated = () => {
  addModal.value = false;
  refreshAfterMutation();
};
const onUpdated = () => {
  editComment.value = null;
  refreshAfterMutation();
};

const doDelete = async () => {
  await API.makeRequest(`/v1/comments/${deleteId.value}`, { method: "DELETE" });
  deleteId.value = null;
  refreshAfterMutation();
};

const patchComment = async (id: number, comment: Record<string, unknown>) => {
  await API.makeRequest(`/v1/comments/${id}`, {
    method: "PUT",
    data: { comment }
  });
  refreshAfterMutation();
};

const toggleVisibility = (note: Comment) =>
  patchComment(note.id, { customer_visible: !note.customer_visible });

const togglePinned = (note: Comment) =>
  patchComment(note.id, { pinned: !note.pinned });

const handleSearch = debounce(async (val: string) => {
  if (!pager.value) return;
  const res = await pager.value.search(val);
  updateData(res.data.data);
}, 250);

watch(search, (val) => handleSearch(val));

watch(filter, (val) => {
  if (!pager.value) return;
  pager.value.filter({ key: "all", value: val ? val.toString() : null });
  fetchData();
});
</script>

<style scoped>
.sort-cont {
  cursor: pointer;
}
.sorting-icon {
  margin-left: 4px;
  color: var(--primary-color);
}
.asc-icon {
  display: inline-block;
  transform: rotate(180deg);
}
</style>
